// Practice Details — the "Card on file & statements" card (was "Collections"): whether
// card payments are set up, the two card-on-file limits, and the name and reply-to
// address on statement emails. Mounted with one line:
//
//   {!readOnly && <CollectionsSettingsCard practiceId={id} isAdmin={isAdmin} />}
//
// Shown when the practice has statements (collections follows statements) — the
// rules routes are deliberately not feature-gated, so an admin can set them before
// switching collections on. Admins edit; everyone else reads. Nothing here sends,
// charges or schedules: the two "charge" switches only decide what a person's
// confirmed charge is allowed to take (A's stripe_service.chargeable).
//
// Patient outreach is off (David, 2026-10-25), so its fields are not shown: the contact
// cadence, the no-show fee (recorded only; Atlas never drafts one) and the same-day
// reschedule exemption (read by nothing). Their stored values are untouched, and
// changedKeys never sends them.

import React, { useEffect, useState } from "react";
import useCapabilities from "../../hooks/useCapabilities";
import {
  NO_SHOW_FEE_CENTS_MAX,
  OVERDUE_DAYS_MAX,
  OVERDUE_DAYS_MIN,
  dollarsToCents,
  fetchRules,
  fetchSettings,
  saveRules,
  subscribeSettings,
} from "./api";
import "./Collections.css";

const FROM_NAME_MAX = 80;
const EMAIL_SHAPE = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;

export function toForm(rules) {
  return {
    chargeNoShowFee: rules.chargeNoShowFee,
    chargeOverdue: rules.chargeOverdue,
    cadenceDays: rules.cadenceDays == null ? "" : String(rules.cadenceDays),
    overdueDays: rules.overdueDays == null ? "" : String(rules.overdueDays),
    // Edited in dollars, stored in cents, like every other amount a biller types.
    noShowFee:
      rules.noShowFeeCents == null
        ? ""
        : (rules.noShowFeeCents / 100).toFixed(2),
    noShowExemptSameDayReschedule: rules.noShowExemptSameDayReschedule,
    statementFromName: rules.statementFromName || "",
    replyTo: rules.replyTo || "",
  };
}

export function validateRulesForm(form) {
  const errors = {};
  const cadence = Number(form.cadenceDays);
  if (!/^\d+$/.test(String(form.cadenceDays)) || cadence < 1 || cadence > 90) {
    errors.cadenceDays = "A whole number of days from 1 to 90.";
  }
  const name = form.statementFromName.trim();
  if (name.length > FROM_NAME_MAX) {
    errors.statementFromName = `At most ${FROM_NAME_MAX} characters.`;
  } else if (/[<>@"]/.test(name)) {
    errors.statementFromName = 'No < > @ or " characters.';
  }
  const overdue = Number(form.overdueDays);
  if (
    !/^\d+$/.test(String(form.overdueDays)) ||
    overdue < OVERDUE_DAYS_MIN ||
    overdue > OVERDUE_DAYS_MAX
  ) {
    errors.overdueDays = `A whole number of days from ${OVERDUE_DAYS_MIN} to ${OVERDUE_DAYS_MAX}.`;
  }
  const fee = dollarsToCents(form.noShowFee);
  if (fee == null || Number.isNaN(fee) || fee > NO_SHOW_FEE_CENTS_MAX) {
    errors.noShowFee = `An amount from $0 to $${(NO_SHOW_FEE_CENTS_MAX / 100).toFixed(0)}. Enter 0 for no fee.`;
  }
  const reply = form.replyTo.trim();
  if (reply && !EMAIL_SHAPE.test(reply)) {
    errors.replyTo = "One email address, or leave blank.";
  }
  return errors;
}

// The subset of the form that differs from what the server last gave us.
export function changedKeys(rules, form) {
  const current = toForm(rules);
  const patch = {};
  if (form.chargeNoShowFee !== current.chargeNoShowFee)
    patch.chargeNoShowFee = form.chargeNoShowFee;
  if (form.chargeOverdue !== current.chargeOverdue)
    patch.chargeOverdue = form.chargeOverdue;
  if (form.cadenceDays !== current.cadenceDays)
    patch.cadenceDays = Number(form.cadenceDays);
  if (form.overdueDays !== current.overdueDays)
    patch.overdueDays = Number(form.overdueDays);
  if (form.noShowFee !== current.noShowFee)
    patch.noShowFeeCents = dollarsToCents(form.noShowFee);
  if (
    form.noShowExemptSameDayReschedule !== current.noShowExemptSameDayReschedule
  )
    patch.noShowExemptSameDayReschedule = form.noShowExemptSameDayReschedule;
  if (form.statementFromName.trim() !== current.statementFromName)
    patch.statementFromName = form.statementFromName.trim();
  if (form.replyTo.trim() !== current.replyTo)
    patch.replyTo = form.replyTo.trim();
  return patch;
}

export default function CollectionsSettingsCard({
  practiceId,
  isAdmin = false,
}) {
  const { features } = useCapabilities(practiceId);
  const shown = !!practiceId && !!(features.collections || features.statements);

  const [settings, setSettings] = useState(null);
  // Bumped when the settings are invalidated elsewhere on this page (a rename), so
  // the effect below re-reads them instead of keeping the old wording on screen.
  const [settingsEpoch, setSettingsEpoch] = useState(0);
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    setRules(null);
    setForm(null);
    setLoadError(null);
    fetchRules(practiceId)
      .then((r) => {
        if (cancelled) return;
        setRules(r);
        setForm(toForm(r));
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [shown, practiceId]);

  useEffect(
    () =>
      subscribeSettings((changed) => {
        if (changed == null || changed === practiceId) {
          setSettingsEpoch((n) => n + 1);
        }
      }),
    [practiceId],
  );

  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    setSettings(null);
    fetchSettings(practiceId)
      .then((body) => {
        if (!cancelled) setSettings(body);
      })
      .catch(() => {
        // The rules above are the card's job; the status strip simply stays absent.
        if (!cancelled) setSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [shown, practiceId, settingsEpoch]);

  if (!shown) return null;

  const errors = form ? validateRulesForm(form) : {};
  const dirty =
    !!rules && !!form && JSON.stringify(toForm(rules)) !== JSON.stringify(form);
  const canSave = isAdmin && dirty && Object.keys(errors).length === 0 && !busy;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setMessage(null);
    try {
      // Only what changed. The route merges a partial body, and sending all five
      // keys would put back the two money switches (`charge_overdue`,
      // `charge_no_show_fee`) as this page read them — quietly undoing another
      // admin's change and widening what a confirmed charge may take.
      const stored = await saveRules(practiceId, changedKeys(rules, form));
      setRules(stored);
      setForm(toForm(stored));
      setMessage({ type: "success", text: "Collection rules saved." });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const disabled = !isAdmin || busy;

  return (
    <div className="integrations-section coll-settings">
      <h3>Card on file &amp; statements</h3>
      <p className="coll-muted coll-settings-lede">
        Nothing here charges or emails anyone. The two switches decide what a
        person's confirmed card charge may take; the last two fields sign the
        statement emails.
      </p>

      {settings && (
        <div className="coll-rules" data-testid="collections-settings">
          <span>
            Card payments:{" "}
            <strong>
              {settings.stripe.enabled ? "set up" : "not set up here"}
            </strong>
          </span>
          <span>
            Paid into:{" "}
            <strong>
              {settings.stripe.connectedAccount
                ? "this practice's own Stripe account"
                : "Atlas's Stripe account"}
            </strong>
          </span>
          {settings.emailWordingVersion != null && (
            <span className="coll-muted">
              statement email wording v{settings.emailWordingVersion}
            </span>
          )}
        </div>
      )}

      {settings && settings.consentText && (
        <details className="coll-consent-details">
          <summary>
            Card-on-file consent wording (v{settings.consentTextVersion}) — what a
            patient agrees to
          </summary>
          <blockquote className="coll-consent-text">
            {settings.consentText}
          </blockquote>
          <p className="coll-field-hint">
            Read-only: the wording is versioned in code, because a consent has to
            record which words the patient agreed to. Changes go through Jordan.
          </p>
        </details>
      )}

      {loadError && (
        <div className="coll-message error" role="alert">
          {loadError}
        </div>
      )}
      {!loadError && !form && <p className="coll-muted">Loading…</p>}

      {form && (
        <form className="coll-settings-form" onSubmit={save}>
          <label className="coll-check">
            <input
              type="checkbox"
              checked={form.chargeNoShowFee}
              disabled={disabled}
              onChange={(e) => set("chargeNoShowFee", e.target.checked)}
            />
            <span>
              Charge no-show fees to a card on file
              <small className="coll-field-hint">
                Off: a visit coded NOSHW can never be charged to a card.
              </small>
            </span>
          </label>
          <label className="coll-check">
            <input
              type="checkbox"
              checked={form.chargeOverdue}
              disabled={disabled}
              onChange={(e) => set("chargeOverdue", e.target.checked)}
            />
            <span>
              Charge overdue balances to a card on file
              <small className="coll-field-hint">
                Off: once the patient's first statement is{" "}
                {form.overdueDays || "—"} days old, none of their balance can be
                charged, however recent the visit; and if no statement ever
                reached them, nothing from a visit older than that.
              </small>
            </span>
          </label>

          <label className="coll-field">
            <span>Overdue after (days)</span>
            <input
              type="text"
              inputMode="numeric"
              value={form.overdueDays}
              disabled={disabled}
              onChange={(e) => set("overdueDays", e.target.value)}
            />
            <small className="coll-field-hint">
              Counted from the first statement that <em>reached</em> the patient —
              a bounced or unconfirmed send does not start it. If none ever
              reached them, it is counted from the visit instead, so a balance
              older than this is still held. Only applies while the switch above
              is off.
            </small>
            {errors.overdueDays && (
              <small className="coll-field-error">{errors.overdueDays}</small>
            )}
          </label>
          <label className="coll-field">
            <span>Statement email from-name</span>
            <input
              type="text"
              maxLength={FROM_NAME_MAX + 10}
              value={form.statementFromName}
              placeholder="the practice name"
              disabled={disabled}
              onChange={(e) => set("statementFromName", e.target.value)}
            />
            {errors.statementFromName && (
              <small className="coll-field-error">
                {errors.statementFromName}
              </small>
            )}
          </label>
          <label className="coll-field">
            <span>Reply-to address</span>
            <input
              type="email"
              value={form.replyTo}
              placeholder="none"
              disabled={disabled}
              onChange={(e) => set("replyTo", e.target.value)}
            />
            {errors.replyTo && (
              <small className="coll-field-error">{errors.replyTo}</small>
            )}
          </label>

          {message && (
            <div className={`coll-message ${message.type}`} role="status">
              <span>{message.text}</span>
              <button
                type="button"
                className="coll-message-close"
                aria-label="Dismiss"
                onClick={() => setMessage(null)}
              >
                ×
              </button>
            </div>
          )}

          {isAdmin ? (
            <div className="coll-settings-actions">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={!canSave}
              >
                {busy ? "Saving…" : "Save rules"}
              </button>
              {dirty && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => setForm(toForm(rules))}
                >
                  Discard changes
                </button>
              )}
            </div>
          ) : (
            <p className="coll-muted">Only an admin can change these.</p>
          )}
        </form>
      )}
    </div>
  );
}
